/**
 * @summary - Component that Provides Asset Type Code Creation Page
 * @author Yuvasri S
 * @since 2024-05-28
 */
import React, { useState, useEffect } from "react";
import { Box, Grid } from "@mui/material";
import CustomTextField from "../commoncomponents/CustomTextField";
import { DefaultCard } from "../../assets/styled-components/card";
import CustomFooter from "../commoncomponents/CustomFooter";
import { useNavigate, useLocation } from "react-router";
import CustomSelectInput from "../commoncomponents/CustomSelectInput";
import axios from "axios";
import CustomDialog from "../commoncomponents/CustomDialog";
import { BemsGraph, FemsGraph, MasterGraph } from "../../utility/apiURLS";
import {
  getAuthToken,
  getcompanyId,
  getFacilityId,
  getCreatedBy,
  getModifiedBy,
} from "../../utility/tokenUtility";
import CustomTextFieldWithSearch from "../commoncomponents/CustomTextFieldWithSearch";
import PageTitle from "../commoncomponents/PageTitle";

const CreateAssetTypeCode = () => {
  const navigate = useNavigate();
  const [service, setService] = useState("");
  const [serviceOptions, setServiceOptions] = useState([]);
  const [classification, setClassification] = useState("");
  const [classificationOptions, setClassificationOptions] = useState([]);
  const [category, setCategory] = useState("");
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoryDesc, setCategoryDesc] = useState("");
  const [typecode, setTypeCode] = useState("");
  const [typedescription, setTypeCodeDesc] = useState("");
  const [typecodeId, setTypeCodeId] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [unsavedAddDialogOpen, setUnsavedAddDialogOpen] = useState(false);
  //cancel validation
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  //Addnew validation
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [errors, setErrors] = useState({
    service: false,
    classification: false,
    category: false,
    typecode: false,
    typedescription: false,
  });

  const [errorMessages, setErrorMessages] = useState({
    service: "",
    classification: "",
    category: "",
    typecode: "",
    typedescription: "",
  });

  const token = getAuthToken("id_token");
  const config = {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  };
  const companyId = getcompanyId("companyId");
  const facilityId = getFacilityId("facilityId");
  const createdBy = getCreatedBy("createdBy");

  const fetchServiceOptions = async () => {
    try {
      const response = await axios.get(
        `${MasterGraph}/api/masters/GetMasters/${companyId}/${facilityId}`,
        config
      );
      const serviceData = response.data.masservicedetails;
      const options = serviceData.map((service) => ({
        value: service.service,
        label: service.service,
        id: service.id,
      }));
      setServiceOptions(options);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error fetching service options:", error);
    }
  };


  const fetchClassificationOptions = async () => {
    try {
      const response = await axios.get(
        `${MasterGraph}/api/masters/GetMasters/${companyId}/${facilityId}`,
        config
      );
      const classificationData = response.data.masassetclassifications;

      // Sort the classificationData array in alphabetical order based on the assetclassification property
      const sortedClassificationData = classificationData.sort((a, b) => {
        if (a.assetclassification.toLowerCase() < b.assetclassification.toLowerCase()) {
          return -1;
        }
        if (a.assetclassification.toLowerCase() > b.assetclassification.toLowerCase()) {
          return 1;
        }
        return 0;
      });

      const options = sortedClassificationData.map((classification) => ({
        value: classification.assetclassification,
        label: classification.assetclassification,
        id: classification.id,
      }));

      setClassificationOptions(options);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error("Error fetching service options:", error);
    }
  };


  const fetchCategoryOptions = async () => {
    const serviceId = service.id;
    try {
      const response = await axios.get(
        `${MasterGraph}/api/graph/GetAssetCategoryFetchList/${companyId}/${serviceId}?size=2000`,
        config
      );
      const categoryData = response.data;
      const options = categoryData.map((category) => ({
        value: category.assetcategorydesc,
        label: `${category.assetcategory} - ${category.assetcategorydesc}`,
        id: category.id,
      }));


      setCategoryOptions(options);
      setHasUnsavedChanges(false);

    } catch (error) {
      console.error("Error fetching category options:", error);
    }
  };

  const handleServiceChange = (newValue) => {
    setHasUnsavedChanges(true);
    setIsSubmitted(false);
    setService(newValue);
    if (newValue) {
      setErrors((prevErrors) => ({ ...prevErrors, service: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, service: "" }));
    }
    else {
      setService('');
    }
  };

  const handleClassificationChange = (newValue) => {
    setHasUnsavedChanges(true);
    setIsSubmitted(false);
    setClassification(newValue);
    if (newValue) {
      setErrors((prevErrors) => ({ ...prevErrors, classification: false }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        classification: "",
      }));
    }
  };

  const handleCategoryChange = (newValue) => {
    setHasUnsavedChanges(true);
    setIsSubmitted(false);
    // Trim the item code and update state
    const trimmedCategory = (newValue ? { ...newValue, label: newValue.label.split(' ')[0].trimRight() } : '');
    setCategory(trimmedCategory);
    setCategoryDesc(newValue.value);

    if (newValue) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        category: false,
      }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        category: "",
      }));
    }
  };

  useEffect(() => {
    fetchServiceOptions();
    fetchClassificationOptions();
  }, []);

  useEffect(() => {
    fetchCategoryOptions();
  }, [service]);

  const handleAtCodeChange = (newValue) => {
    setHasUnsavedChanges(true);
    setIsSubmitted(false);
    setTypeCode(newValue);

    const regex = /^[a-zA-Z0-9 /-]*$/;

    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, typecode: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        typecode: "Invalid characters in Type Code",
      }));
    } else if (newValue.startsWith(" ")) {
      setErrors((prevErrors) => ({ ...prevErrors, typecode: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        typecode: "Space not allowed at the beginning",
      }));
    } else if (newValue.includes("  ")) {
      setErrors((prevErrors) => ({ ...prevErrors, typecode: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        typecode: "Double Space not allowed",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, typecode: false }));
      setErrorMessages((prevMessages) => ({ ...prevMessages, typecode: "" }));
    }
  };

  const handleAtDescChange = (newValue) => {
    setHasUnsavedChanges(true);
    setIsSubmitted(false);
    setTypeCodeDesc(newValue);

    const regex = /^[a-zA-Z0-9,!@#$%&*()_\- .]*$/;

    if (!regex.test(newValue)) {
      setErrors((prevErrors) => ({ ...prevErrors, typedescription: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        typedescription: "Invalid characters in Type Code Description",
      }));
    } else if (newValue.startsWith(" ")) {
      setErrors((prevErrors) => ({ ...prevErrors, typedescription: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        typedescription: "Space not allowed at the beginning",
      }));
    } else if (newValue.includes("  ")) {
      setErrors((prevErrors) => ({ ...prevErrors, typedescription: true }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        typedescription: "Double Space not allowed",
      }));
    } else {
      setErrors((prevErrors) => ({ ...prevErrors, typedescription: false }));
      setErrorMessages((prevMessages) => ({
        ...prevMessages,
        typedescription: "",
      }));
    }
  };
  const handleSubmit = (e) => {
    e.preventDefault();

    let formIsValid = true;
    const newErrors = {
      service: false,
      classification: false,
      category: false,
      typecode: false,
      typedescription: false,
    };
    const newErrorMessages = {
      service: "",
      classification: "",
      category: "",
      typecode: "",
      typedescription: "",
    };

    if (!service) {
      formIsValid = false;
      newErrors.service = true;
      newErrorMessages.service = "Service is required";
    }
    if (!classification) {
      formIsValid = false;
      newErrors.classification = true;
      newErrorMessages.classification = "Asset Classification is required";
    }
    if (!category.label) {
      formIsValid = false;
      newErrors.category = true;
      newErrorMessages.category = "Asset Category is required";
    }

    if (!typecode) {
      formIsValid = false;
      newErrors.typecode = true;
      newErrorMessages.typecode = "Type Code is required";
    } else if (typecode.startsWith(" ")) {
      formIsValid = false;
      newErrors.typecode = true;
      newErrorMessages.typecode = "Space not allowed at the beginning";
    } else if (typecode.includes("  ")) {
      formIsValid = false;
      newErrors.typecode = true;
      newErrorMessages.typecode = "Double Space not allowed";
    } else if (!/^[a-zA-Z0-9 /-]*$/.test(typecode)) {
      formIsValid = false;
      newErrors.typecode = true;
      newErrorMessages.typecode = "Invalid Characters in Type Code";
    }

    if (!typedescription) {
      formIsValid = false;
      newErrors.typedescription = true;
      newErrorMessages.typedescription = "Type Code Description is required";
    } else if (typedescription.startsWith(" ")) {
      formIsValid = false;
      newErrors.typedescription = true;
      newErrorMessages.typedescription = "Space not allowed at the beginning";
    } else if (typedescription.includes("  ")) {
      formIsValid = false;
      newErrors.typedescription = true;
      newErrorMessages.typedescription = "Double Space not allowed";
    } else if (!/^[a-zA-Z0-9,!@#$%&*()_\- .]*$/.test(typedescription)) {
      formIsValid = false;
      newErrors.typedescription = true;
      newErrorMessages.typedescription =
        "Invalid Characters in Type Code Description";
    }

    setErrors(newErrors);
    setErrorMessages(newErrorMessages);

    if (formIsValid) {
      handleSave();
      setHasUnsavedChanges(false);
    }
  };
  const handleSave = async () => {
    try {

      const payload = {
        typecode,
        typedescription,
        id: typecodeId,
        masservicedetails: {
          service: service.label,
          servicedesc: service.value,
          id: service.id,
          masassetcategories: [],
          masassettypecodes: [],
          maslicensecertheaders: [],
          mascalibrationcertheaders: [],
          maslicensetypecodedetails: [],
        },
        masassetcategory: {
          assetcategory: category.label,
          assetcategorydesc: category.value,
          id: category.id,
          companyid: companyId,
          masassettypecodes: [],
        },
        masassetclassification: {
          assetclassification: classification.value,
          id: classification.id,
          isvisible: true,
          masassettypecodes: [],
        },
        isppmrequired: true,
        createdby: createdBy,
        masimscompanyitemdetails: [],
      };

      await axios.post(
        `${MasterGraph}/api/masassettypecodes`,
        payload,
        config
      );
      setIsSubmitted(true);
      setDialogOpen(true);
    } catch (error) {
      console.error("Error saving data:", error);
      if (error.response && error.response.status === 400) {
        setErrorMessage("Asset Type Code Already Exists");
      } else {
        setErrorMessage(
          "An unexpected error occurred. Please try again later."
        );
      }
      setErrorDialogOpen(true);
    }
  };

  const handleCancel = () => {
    if (hasUnsavedChanges) {
      setCancelDialogOpen(true);
    }
    else {
      navigate(-1, { replace: true });
    }
  };

  const handleCancelConfirm = () => {
    setCancelDialogOpen(false);
    navigate(-1, { replace: true });
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
  };

  const handleCancelDialogClose = () => {
    setCancelDialogOpen(false);
  };

  const handleAdd = () => {
    if (!isSubmitted && (service || classification?.id || category?.id || categoryDesc || typecode || typedescription)) {
      setUnsavedAddDialogOpen(true);
    } else {
      clearForm();
    }
  };

  const clearForm = () => {
    setService("");
    setClassification("");
    setCategory({ value: '', label: '' });
    setCategoryDesc("");
    setTypeCode("");
    setTypeCodeDesc("");
    setTypeCodeId("");
    setErrors({
      service: false,
      classification: false,
      category: false,
      typedescription: false,
    });
    setErrorMessages({
      service: "",
      classification: "",
      category: "",
      typedescription: "",
    });
  };

  const handleUnsavedAddDialogClose = () => {
    setUnsavedAddDialogOpen(false);
  };

  const handleUnsavedAddConfirm = () => {
    clearForm();
    setUnsavedAddDialogOpen(false);
  };

  return (
    <>
      <PageTitle
        title={"Create Asset Type Code"}
      />

      <DefaultCard sx={{ height: "100%" }}>
        <form onSubmit={handleSubmit} style={{ position: "relative" }}>
          <Grid container gap={3}>
            <Grid item xs={6} md={3.8}>
              <CustomSelectInput
                id="service-label"
                label="Service"
                value={service}
                options={serviceOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={handleServiceChange}
                isMandatoryField={true}
                error={errors.service}
                errorMessage={errorMessages.service}
                autoFocus
              />
            </Grid>
            <Grid item xs={6} md={3.8}>
              <CustomSelectInput
                maxlength={10}
                id="classification-label"
                label="Asset Classification"
                value={classification}
                options={classificationOptions}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) =>
                  option.value === value.value
                }
                onChange={handleClassificationChange}
                isMandatoryField={true}
                error={errors.classification}
                errorMessage={errorMessages.classification}
              />
            </Grid>
            <Grid item xs={6} md={3.8}>
              <CustomTextFieldWithSearch
                label="Asset Category"
                id="asset-category"
                value={category}
                options={categoryOptions}
                onChange={handleCategoryChange}
                isMandatoryField={true}
                error={errors.category}
                errorMessage={errorMessages.category}
              />
            </Grid>

            <Grid item xs={6} md={3.8}>
              <CustomTextField
                maxlength={225}
                label="Asset Category Description"
                id="category-desc"
                value={categoryDesc}
                disabled={true}
                placeholder=""
              />
            </Grid>
            <Grid item xs={6} md={3.8}>
              <CustomTextField
                maxlength={10}
                label="Type Code"
                id="type-code"
                value={typecode}
                onChange={handleAtCodeChange}
                isMandatoryField={true}
                error={errors.typecode}
                errorMessage={errorMessages.typecode}
              />
            </Grid>
            <Grid item xs={6} md={3.8}>
              <CustomTextField
                maxlength={255}
                label="Type Code Description"
                id="type-code-description"
                value={typedescription}
                onChange={handleAtDescChange}
                isMandatoryField={true}
                error={errors.typedescription}
                errorMessage={errorMessages.typedescription}
              />
            </Grid>
          </Grid>
        </form>
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: { xs: "center", md: "end" },
          }}
        >
          <CustomFooter
            cancelClick={handleCancel}
            saveClick={handleSubmit}
            addClick={handleAdd}
            showAddButton={true}
            buttonLabel={"Save"}
          />
        </Box>
      </DefaultCard>

      <CustomDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        onConfirm={handleDialogClose}
        title="Success"
        confirmBtn="Ok"
        content="Data saved successfully."
      />

      <CustomDialog
        open={cancelDialogOpen}
        onClose={handleCancelDialogClose}
        onConfirm={handleCancelConfirm}
        content="Record(s) not Saved/Updated. Do you want to continue?"
        cancelBtn="No"
        confirmBtn="Yes"
      />
      <CustomDialog
        open={unsavedAddDialogOpen}
        content="Record(s) not Saved/Updated. Do you want to continue?"
        onConfirm={handleUnsavedAddConfirm}
        onClose={handleUnsavedAddDialogClose}
        cancelBtn="No"
        confirmBtn="Yes"
      />
      <CustomDialog
        open={errorDialogOpen}
        onClose={() => setErrorDialogOpen(false)}
        onConfirm={() => setErrorDialogOpen(false)}
        confirmBtn="Ok"
        content={errorMessage}
      />
    </>
  );
};

export default CreateAssetTypeCode;
